import type { MutationResult } from '../../api/contract';
import { createAppError } from '../../api/errors';
import { adminUserFixtures, permissionFixtures, permissionProfileFixtures } from './fixtures';
import type { AdminUsersRepository } from './repository';
import type {
  AdminUserDetail,
  AdminUserListQuery,
  InviteAdminUserInput,
  PermissionProfile,
  PermissionProfileListQuery,
} from './types';

export interface MockAdminUsersRepositoryOptions {
  readonly mode?: 'data' | 'empty' | 'error';
}

// Stable, human-readable route identifiers resolve to the first fixture without duplicating
// fixture rows or altering list/pagination totals.
const adminUserDetailAliases: Readonly<Record<string, string>> = {
  'admin-a': adminUserFixtures[0]!.id,
};

export class MockAdminUsersRepository implements AdminUsersRepository {
  readonly #mode: 'data' | 'empty' | 'error';
  #users: AdminUserDetail[];
  #profiles: PermissionProfile[];

  constructor(options: MockAdminUsersRepositoryOptions = {}) {
    this.#mode = options.mode ?? 'data';
    this.#users = this.#mode === 'empty' ? [] : adminUserFixtures.map(cloneUser);
    this.#profiles = this.#mode === 'empty' ? [] : permissionProfileFixtures.map(cloneProfile);
  }

  async listUsers(query: AdminUserListQuery, signal: AbortSignal) {
    this.#available(signal);
    const filtered = sortUsers(
      this.#users.filter((user) => userMatches(user, query)),
      query.sort,
      query.order,
    );
    const start = (query.page - 1) * query.pageSize;
    return {
      items: filtered.slice(start, start + query.pageSize).map(cloneUser),
      total: filtered.length,
      requestId: 'mock-admin-users-list',
    };
  }
  async getUser(userId: string, signal: AbortSignal) {
    this.#available(signal);
    return cloneUser(this.#user(userId));
  }
  inviteUser(input: InviteAdminUserInput): Promise<MutationResult<AdminUserDetail>> {
    this.#available();
    const timestamp = '2026-07-23T15:00:00+07:00';
    const created: AdminUserDetail = {
      id: mockUuid(20 + this.#users.length),
      subject: input.subject,
      username: input.username,
      email: input.email ?? null,
      displayName: input.displayName,
      role: input.access.role,
      status: 'INVITED',
      partnerId: 'partnerId' in input.access ? input.access.partnerId : null,
      agentId: 'agentId' in input.access ? input.access.agentId : null,
      permissionProfile:
        'permissionProfileId' in input.access
          ? profileSummary(this.#profile(input.access.permissionProfileId))
          : null,
      lastLoginAt: null,
      createdAt: timestamp,
      createdBy: actor,
      updatedAt: timestamp,
      updatedBy: actor,
    };
    this.#users = [created, ...this.#users];
    return Promise.resolve(result(created, 'mock-admin-user-invite'));
  }
  updateUser(userId: string, input: { readonly displayName: string; readonly reason: string }) {
    const updated = { ...this.#user(userId), displayName: input.displayName };
    this.#replaceUser(updated);
    return Promise.resolve(result(updated, 'mock-admin-user-update'));
  }
  changeAccess(userId: string, input: Parameters<AdminUsersRepository['changeAccess']>[1]) {
    const current = this.#user(userId);
    const updated: AdminUserDetail = {
      ...current,
      role: input.access.role,
      partnerId: 'partnerId' in input.access ? input.access.partnerId : null,
      agentId: 'agentId' in input.access ? input.access.agentId : null,
      permissionProfile:
        'permissionProfileId' in input.access
          ? profileSummary(this.#profile(input.access.permissionProfileId))
          : null,
    };
    this.#replaceUser(updated);
    return Promise.resolve(result(updated, 'mock-admin-user-access'));
  }
  changeStatus(userId: string, input: Parameters<AdminUsersRepository['changeStatus']>[1]) {
    const updated = { ...this.#user(userId), status: input.status };
    this.#replaceUser(updated);
    return Promise.resolve(result(updated, 'mock-admin-user-status'));
  }
  async listPermissions(signal: AbortSignal) {
    this.#available(signal);
    return permissionFixtures.map((permission) => ({ ...permission }));
  }
  async listProfiles(query: PermissionProfileListQuery, signal: AbortSignal) {
    this.#available(signal);
    const filtered = sortProfiles(
      this.#profiles.filter((profile) => profileMatches(profile, query)),
      query.sort,
      query.order,
    );
    const start = (query.page - 1) * query.pageSize;
    return {
      items: filtered.slice(start, start + query.pageSize).map(cloneProfile),
      total: filtered.length,
      requestId: 'mock-permission-profiles-list',
    };
  }
  async getProfile(profileId: string, signal: AbortSignal) {
    this.#available(signal);
    return cloneProfile(this.#profile(profileId));
  }
  createProfile(input: Parameters<AdminUsersRepository['createProfile']>[0]) {
    const timestamp = '2026-07-23T15:00:00+07:00';
    const created: PermissionProfile = {
      id: mockUuid(900 + this.#profiles.length),
      partnerId: input.partnerId,
      agentId: input.agentId,
      name: input.name,
      status: 'ACTIVE',
      permissions: [...input.permissions],
      assignedUserCount: 0,
      createdAt: timestamp,
      createdBy: actor,
      updatedAt: timestamp,
      updatedBy: actor,
    };
    this.#profiles = [created, ...this.#profiles];
    return Promise.resolve(result(created, 'mock-permission-profile-create'));
  }
  updateProfile(profileId: string, input: Parameters<AdminUsersRepository['updateProfile']>[1]) {
    const updated = {
      ...this.#profile(profileId),
      name: input.name,
      permissions: [...input.permissions],
    };
    this.#replaceProfile(updated);
    return Promise.resolve(result(updated, 'mock-permission-profile-update'));
  }
  changeProfileStatus(
    profileId: string,
    input: Parameters<AdminUsersRepository['changeProfileStatus']>[1],
  ) {
    const updated = { ...this.#profile(profileId), status: input.status };
    this.#replaceProfile(updated);
    return Promise.resolve(result(updated, 'mock-permission-profile-status'));
  }

  #available(signal?: AbortSignal) {
    if (signal?.aborted) throw new DOMException('Request aborted', 'AbortError');
    if (this.#mode === 'error')
      throw appError(503, 'Mock Admin User data is unavailable', 'mock-admin-users-error');
  }
  #user(id: string) {
    const resolvedId = adminUserDetailAliases[id] ?? id;
    const value = this.#users.find((item) => item.id === resolvedId);
    if (!value) throw appError(404, 'Backoffice user not found', 'mock-admin-user-not-found');
    return value;
  }
  #profile(id: string) {
    const value = this.#profiles.find((item) => item.id === id);
    if (!value) throw appError(404, 'Permission profile not found', 'mock-profile-not-found');
    return value;
  }
  #replaceUser(value: AdminUserDetail) {
    this.#users = this.#users.map((item) => (item.id === value.id ? value : item));
  }
  #replaceProfile(value: PermissionProfile) {
    this.#profiles = this.#profiles.map((item) => (item.id === value.id ? value : item));
  }
}

function sortUsers(
  users: readonly AdminUserDetail[],
  sort: AdminUserListQuery['sort'],
  order: AdminUserListQuery['order'],
): AdminUserDetail[] {
  return sortBy(users, (user) => user[sort], order);
}

function sortProfiles(
  profiles: readonly PermissionProfile[],
  sort: PermissionProfileListQuery['sort'],
  order: PermissionProfileListQuery['order'],
): PermissionProfile[] {
  return sortBy(profiles, (profile) => profile[sort], order);
}

function sortBy<T>(
  values: readonly T[],
  select: (value: T) => string | null,
  order: 'asc' | 'desc',
): T[] {
  const direction = order === 'asc' ? 1 : -1;
  return [...values].sort((left, right) => {
    const compared = (select(left) ?? '').localeCompare(select(right) ?? '');
    return compared * direction;
  });
}

function userMatches(user: AdminUserDetail, query: AdminUserListQuery) {
  const f = query.filters;
  return (
    (!f.role || user.role === f.role) &&
    (!f.status || user.status === f.status) &&
    includes(user.partnerId, f.partnerId) &&
    includes(user.agentId, f.agentId) &&
    includes(user.permissionProfile?.id ?? null, f.permissionProfileId) &&
    includes(user.username, f.username) &&
    includes(user.email, f.email)
  );
}
function profileMatches(profile: PermissionProfile, query: PermissionProfileListQuery) {
  const f = query.filters;
  return (
    includes(profile.partnerId, f.partnerId) &&
    includes(profile.agentId, f.agentId) &&
    (!f.status || profile.status === f.status) &&
    includes(profile.name, f.name)
  );
}
function includes(value: string | null, search: string) {
  return (
    search.trim() === '' ||
    (value ?? '').toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  );
}
function cloneUser(user: AdminUserDetail): AdminUserDetail {
  return {
    ...user,
    permissionProfile: user.permissionProfile === null ? null : { ...user.permissionProfile },
  };
}
function cloneProfile(profile: PermissionProfile): PermissionProfile {
  return { ...profile, permissions: [...profile.permissions] };
}
function profileSummary(profile: PermissionProfile) {
  return { id: profile.id, name: profile.name, status: profile.status };
}
function result<T>(data: T, requestId: string): MutationResult<T> {
  return { data, requestId };
}
function appError(status: number, message: string, requestId: string) {
  return createAppError({
    status,
    code: 'MOCK_ADMIN_USERS_ERROR',
    message,
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}
const actor = '00000000-0000-4000-8000-000000000001';
function mockUuid(sequence: number) {
  return `00000000-0000-4000-8000-${String(sequence).padStart(12, '0')}`;
}
